import { type AccountSchedulingSettings, ApprovalMode, type Booking, type UserSchedulingSettings } from '../../models/Scheduling'

export type SchedulingTabKey = 'requests' | 'suggested' | 'waiting' | 'scheduled'

export const SCHEDULING_TAB_KEYS: SchedulingTabKey[] = ['requests', 'suggested', 'waiting', 'scheduled']

export const resolveActiveTabKey = (requestedTab: string | undefined, showRequestsTab: boolean): SchedulingTabKey => {
  if (SCHEDULING_TAB_KEYS.includes(requestedTab as SchedulingTabKey) && (requestedTab !== 'requests' || showRequestsTab)) {
    return requestedTab as SchedulingTabKey
  }
  return showRequestsTab ? 'requests' : 'suggested'
}

export const isRequestsTabVisible = (
  accountSettings: AccountSchedulingSettings | undefined,
  userSettings: UserSchedulingSettings | undefined,
  pendingRequestCount: number,
): boolean => {
  const accountMode = accountSettings?.approval.booking
  const effectiveMode = accountMode != null && accountMode !== ApprovalMode.USERS_DECIDE ? accountMode : userSettings?.bookingRules.approval.booking
  return effectiveMode !== ApprovalMode.NEVER_REQUIRE || pendingRequestCount > 0
}

export interface BookingMonthGroup {
  monthKey: string
  monthLabel: string
  bookings: Booking[]
}

export const startOfCurrentMinute = (): Date => {
  const now = new Date()
  now.setSeconds(0, 0)
  return now
}

const bookingSortTime = (booking: Booking): number => (booking.start ? new Date(booking.start).getTime() : Number.MAX_SAFE_INTEGER)

// Groups scheduled bookings into ascending month buckets with "APRIL 2026"-style labels
export const groupBookingsByMonth = (bookings: Booking[]): BookingMonthGroup[] => {
  const sorted = [...bookings].sort((a, b) => bookingSortTime(a) - bookingSortTime(b))
  const groups: BookingMonthGroup[] = []
  sorted.forEach((booking) => {
    if (!booking.start) {
      return
    }
    const startAt = new Date(booking.start)
    const monthKey = `${startAt.getFullYear()}-${String(startAt.getMonth() + 1).padStart(2, '0')}`
    const lastGroup = groups[groups.length - 1]
    if (lastGroup && lastGroup.monthKey === monthKey) {
      lastGroup.bookings.push(booking)
    } else {
      groups.push({
        monthKey,
        monthLabel: startAt.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }).toUpperCase(),
        bookings: [booking],
      })
    }
  })
  return groups
}
